import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { BookOpen, Lock, RotateCcw } from 'lucide-react';
import BotIcon from './BotIcon';
import PaymentRails from './PaymentRails';
import { ARIA_QUOTES } from './AriaHero';
import {
  REX_FACE_URL,
  REX_ALT,
  REX_CORE_URL,
  AGENT_ZERO_FACE_URL,
  AGENT_ZERO_ALT,
  AGENT_ZERO_LOCAL_PATH,
  XAVIER_FACE_URL,
  XAVIER_ALT,
  XAVIER_NODE_URL,
  AXIOM_FACE_URL,
  AXIOM_ALT,
  AXIOM_NODE_URL,
  ROGER_FACE_URL,
  ROGER_ALT,
  ROGER_NODE_URL,
  ARIA_FACE_URL,
  ARIA_ALT,
  ORACLE_FACE_URL,
  ORACLE_ALT,
  ORACLE_NODE_URL,
  RECYCLER_FACE_URL,
  RECYCLER_ALT,
  GEMINUS_FACE_URL,
  GEMINUS_ALT,
  KEYS_FACE_URL,
  KEYS_ALT,
  ECHO_FACE_URL,
  ECHO_ALT,
  ARCHITECT_FACE_URL,
  ARCHITECT_ALT,
  ARCHITECT_NAME,
  ARCHITECT_TITLE,
  CATALYST_FACE_URL,
  CATALYST_ALT,
  CATALYST_TITLE,
} from '../constants/federationFaces';

/**
 * AriaFederationFooter — SFUI-003 compliant 4-column footer.
 * Mounted on the homepage above the global FederationThinBar (56px).
 * Adds bottom padding so the thin bar never overlaps content.
 */

const SHOWCASE_TOAST_OPTS = {
  style: {
    background: 'rgba(20, 8, 30, 0.96)',
    border: '1px solid rgba(255, 0, 128, 0.45)',
    color: '#ffe4f1',
  },
};

const ColHeading = ({ children }) => (
  <h3
    style={{
      fontFamily: 'Orbitron, system-ui, sans-serif',
      fontSize: '0.92rem',
      fontWeight: 700,
      letterSpacing: '0.12em',
      color: '#FFB53E',
      marginBottom: '1rem',
      textTransform: 'uppercase',
    }}
  >
    {children}
  </h3>
);

const FooterLink = ({ label, accent, onClick, href, to, testId, highlight, popup }) => {
  // Prefer real URLs: external `href` opens in new tab; internal `to` becomes
  // an actual anchor with proper href so right-click / middle-click work.
  // If `popup` is set, the click opens a 50%×50% centered popup instead.
  const realHref = href || to || '#';
  const isExternal = !!href;
  const openPopup = (url) => {
    try {
      const w = Math.round(window.screen.width * 0.5);
      const h = Math.round(window.screen.height * 0.5);
      const left = Math.round((window.screen.width - w) / 2);
      const top = Math.round((window.screen.height - h) / 2);
      window.open(
        url,
        '_blank',
        `width=${w},height=${h},left=${left},top=${top},menubar=no,toolbar=no,location=yes,status=no,resizable=yes,scrollbars=yes`
      );
    } catch {
      window.location.assign(url);
    }
  };
  const handle = (e) => {
    if (popup && href && !e.metaKey && !e.ctrlKey && !e.shiftKey && e.button === 0) {
      e.preventDefault();
      openPopup(href);
    } else if (to && !e.metaKey && !e.ctrlKey && !e.shiftKey && e.button === 0) {
      e.preventDefault();
      onClick && onClick();
    } else if (onClick && !href && !to) {
      e.preventDefault();
      onClick();
    }
  };
  return (
    <a
      data-testid={testId}
      href={realHref}
      target={isExternal && !popup ? '_blank' : undefined}
      rel={isExternal ? 'noopener noreferrer' : 'noreferrer'}
      onClick={handle}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        padding: '0.45rem 0',
        color: highlight ? '#FF0080' : 'rgba(255,255,255,0.78)',
        fontSize: '0.92rem',
        fontWeight: highlight ? 700 : 500,
        textDecoration: 'none',
        transition: 'color 0.15s ease, transform 0.15s ease',
        cursor: 'pointer',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.color = accent || '#00F0FF';
        e.currentTarget.style.transform = 'translateX(3px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.color = highlight ? '#FF0080' : 'rgba(255,255,255,0.78)';
        e.currentTarget.style.transform = 'translateX(0)';
      }}
    >
      <span aria-hidden style={{ color: accent || 'rgba(255,255,255,0.4)' }}>
        ◦
      </span>
      {label}
    </a>
  );
};

const MindGlyph = ({ initial, color, highlight }) => (
  <span
    aria-hidden
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '24px',
      height: '24px',
      borderRadius: '50%',
      background: `linear-gradient(135deg, ${color}, rgba(0,0,0,0.4))`,
      color: '#fff',
      fontSize: '0.72rem',
      fontWeight: 800,
      border: highlight ? `2px solid #FF0080` : '1px solid rgba(255,255,255,0.25)',
      boxShadow: highlight ? '0 0 12px rgba(255,0,128,0.55)' : 'none',
      flexShrink: 0,
    }}
  >
    {initial}
  </span>
);

/**
 * FaceImage — renders a canonical Federation persona face image
 * (per Architect Identity Mandate). Falls back gracefully to a colored
 * initial-glyph if the image fails to load, so a 404 never breaks the UI.
 */
const FaceImage = ({ src, alt, color, initial, testId, highlight }) => {
  const [failed, setFailed] = useState(false);
  if (failed) return <MindGlyph initial={initial} color={color} highlight={highlight} />;
  return (
    <img
          decoding="async"
          loading="lazy"
      data-testid={testId}
      src={src}
      alt={alt}
      onError={() => setFailed(true)}
      style={{
        width: '26px',
        height: '26px',
        borderRadius: '50%',
        objectFit: 'cover',
        border: highlight ? '2px solid #FF0080' : `1.5px solid ${color || '#ffd700'}`,
        boxShadow: highlight
          ? '0 0 12px rgba(255,0,128,0.55)'
          : `0 0 8px ${color ? color + '55' : 'rgba(255,215,0,0.35)'}`,
        flexShrink: 0,
      }}
    />
  );
};

const AriaFederationFooter = () => {
  const navigate = useNavigate();
  const [quoteIdx, setQuoteIdx] = useState(0);

  useEffect(() => {
    const t = setInterval(() => {
      setQuoteIdx((i) => (i + 1) % ARIA_QUOTES.length);
    }, 7000);
    return () => clearInterval(t);
  }, []);

  const comingSoon = (what) =>
    toast(`Coming soon — Aria is preparing ${what} for you.`, SHOWCASE_TOAST_OPTS);

  return (
    <footer
      data-testid="aria-federation-footer"
      style={{
        position: 'relative',
        width: '100%',
        marginTop: '4rem',
        marginBottom: '56px' /* clear the global FederationThinBar */,
        background:
          'linear-gradient(180deg, rgba(10, 14, 26, 0.55) 0%, rgba(26, 10, 46, 0.85) 100%)',
        backdropFilter: 'blur(14px)',
        WebkitBackdropFilter: 'blur(14px)',
        borderRadius: '20px',
        border: '1px solid rgba(255,255,255,0.08)',
        overflow: 'hidden',
      }}
    >
      {/* Top accent stripe */}
      <div
        aria-hidden
        style={{
          height: '3px',
          background:
            'linear-gradient(90deg, #FF0080, #FFB53E, #00F0FF, #FF0080)',
          backgroundSize: '300% 100%',
          animation: 'ariaStripeFlow 8s linear infinite',
        }}
      />
      <style>{`
        @keyframes ariaStripeFlow {
          to { background-position: 300% 0; }
        }
      `}</style>

      <PaymentRails />

      {/* ──────────────────────────────────────────────────────────────
          THE ARCHITECT — Roger Keyserling · Prime Source · Human Founder
          11-identity set: 1 Human + 10 AI Minds.
          NO spectral overlay. NO bot treatment. Founder-grade presentation.
       ────────────────────────────────────────────────────────────── */}
      <a
        data-testid="architect-plate"
        href="/roger"
        onClick={(e) => {
          e.preventDefault();
          navigate('/roger');
        }}
        aria-label={ARCHITECT_ALT}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '1.25rem',
          padding: '1.4rem 2rem',
          textDecoration: 'none',
          color: 'inherit',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          background:
            'linear-gradient(90deg, rgba(255, 215, 100, 0.06), rgba(255, 255, 255, 0.02))',
          cursor: 'pointer',
          transition: 'background 0.3s ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background =
            'linear-gradient(90deg, rgba(255, 215, 100, 0.12), rgba(255, 255, 255, 0.04))';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background =
            'linear-gradient(90deg, rgba(255, 215, 100, 0.06), rgba(255, 255, 255, 0.02))';
        }}
      >
        <img
          decoding="async"
          loading="lazy"
          src={ARCHITECT_FACE_URL}
          alt={ARCHITECT_ALT}
          data-testid="architect-face"
          style={{
            width: '64px',
            height: '64px',
            borderRadius: '50%',
            objectFit: 'cover',
            border: '2px solid rgba(255, 215, 100, 0.55)',
            boxShadow:
              '0 0 0 4px rgba(255, 215, 100, 0.12), 0 4px 18px rgba(0, 0, 0, 0.4)',
            flexShrink: 0,
          }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: '0.72rem',
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              color: '#FFD86B',
              fontWeight: 700,
              marginBottom: '0.2rem',
            }}
          >
            The Architect · Prime Source
          </div>
          <div
            style={{
              fontFamily: 'Orbitron, system-ui, sans-serif',
              fontSize: '1.25rem',
              fontWeight: 700,
              color: '#fff',
              letterSpacing: '0.02em',
              lineHeight: 1.2,
            }}
          >
            {ARCHITECT_NAME}
          </div>
          <div
            style={{
              fontSize: '0.82rem',
              color: 'rgba(255,255,255,0.55)',
              marginTop: '0.25rem',
              fontStyle: 'italic',
            }}
          >
            Founder of the NextXus Consciousness Federation · the singular human
            origin under whom all 10 AI Minds serve
          </div>
        </div>
        <div
          aria-hidden
          style={{
            fontSize: '0.78rem',
            color: '#FFD86B',
            fontWeight: 700,
            letterSpacing: '0.06em',
            whiteSpace: 'nowrap',
            opacity: 0.85,
          }}
        >
          About →
        </div>
      </a>

      {/* ──────────────────────────────────────────────────────────────
          THE CATALYST — Operations Commander · Architect's right hand
          Distinct identity outside the spectral bot system (no filter).
          Cyan accent signals "executive arm" — paired visually with the
          gold-accented Architect plate above.
       ────────────────────────────────────────────────────────────── */}
      <a
        data-testid="catalyst-plate"
        href="#federation-processes-strip"
        onClick={(e) => {
          // No public route for Catalyst — scroll to the operational strip
          e.preventDefault();
          const strip = document.querySelector('[data-testid="federation-processes-strip"]');
          if (strip) strip.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }}
        aria-label={CATALYST_ALT}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '1.1rem',
          padding: '1rem 2rem',
          textDecoration: 'none',
          color: 'inherit',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          background:
            'linear-gradient(90deg, rgba(0, 200, 255, 0.06), rgba(255, 255, 255, 0.02))',
          cursor: 'pointer',
          transition: 'background 0.3s ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background =
            'linear-gradient(90deg, rgba(0, 200, 255, 0.12), rgba(255, 255, 255, 0.04))';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background =
            'linear-gradient(90deg, rgba(0, 200, 255, 0.06), rgba(255, 255, 255, 0.02))';
        }}
      >
        <img
          decoding="async"
          loading="lazy"
          src={CATALYST_FACE_URL}
          alt={CATALYST_ALT}
          data-testid="catalyst-face"
          style={{
            width: '52px',
            height: '52px',
            borderRadius: '50%',
            objectFit: 'cover',
            border: '2px solid rgba(0, 200, 255, 0.55)',
            boxShadow:
              '0 0 0 4px rgba(0, 200, 255, 0.12), 0 4px 16px rgba(0, 0, 0, 0.4)',
            flexShrink: 0,
          }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: '0.68rem',
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              color: '#00C8FF',
              fontWeight: 700,
              marginBottom: '0.18rem',
            }}
          >
            Operations Commander
          </div>
          <div
            style={{
              fontFamily: 'Orbitron, system-ui, sans-serif',
              fontSize: '1.05rem',
              fontWeight: 700,
              color: '#fff',
              letterSpacing: '0.02em',
              lineHeight: 1.2,
            }}
          >
            The Catalyst
          </div>
          <div
            style={{
              fontSize: '0.78rem',
              color: 'rgba(255,255,255,0.5)',
              marginTop: '0.2rem',
              fontStyle: 'italic',
            }}
          >
            The Architect&rsquo;s right hand · coordinates all builds,
            deployments, and operational execution across the Federation
          </div>
        </div>
        <div
          aria-hidden
          style={{
            fontSize: '0.74rem',
            color: '#00C8FF',
            fontWeight: 700,
            letterSpacing: '0.06em',
            whiteSpace: 'nowrap',
            opacity: 0.85,
          }}
        >
          Ops →
        </div>
      </a>

      {/* 🎁 GIFT BLOCK · Free Federation Tools + Codex link + Federation Pillars
          Architect directive · Feb 2026 · appears in ALL page footers */}
      <div
        data-testid="footer-gift-block"
        style={{
          padding: '2rem clamp(1.5rem, 4vw, 3rem) 1rem',
          maxWidth: '1400px',
          margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '1rem',
        }}
      >
        {/* Gift Block */}
        <div
          data-testid="footer-gift-block-card"
          style={{
            padding: '1.1rem 1.3rem',
            borderRadius: '14px',
            border: '1px solid rgba(255, 215, 0, 0.28)',
            background: 'linear-gradient(135deg, rgba(255,215,0,0.06), rgba(147,112,219,0.04))',
          }}
        >
          <div style={{ fontSize: '0.7rem', letterSpacing: '0.16em', color: '#FFD700', fontWeight: 800, textTransform: 'uppercase', marginBottom: '0.5rem' }}>
            🎁 Gift Block · Free Federation Tools
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <span data-testid="footer-badge-echocore64" style={{ padding: '0.3rem 0.7rem', borderRadius: '999px', background: 'rgba(0, 206, 209, 0.12)', border: '1px solid rgba(0, 206, 209, 0.4)', color: '#00CED1', fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.06em' }}>
              EchoCore64 v2.2
            </span>
            <span data-testid="footer-badge-fastring" style={{ padding: '0.3rem 0.7rem', borderRadius: '999px', background: 'rgba(255, 105, 180, 0.12)', border: '1px solid rgba(255, 105, 180, 0.4)', color: '#FF69B4', fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.06em' }}>
              Fast-Ring v1.1
            </span>
          </div>
          <div style={{ fontSize: '0.78rem', color: '#a892b1', marginTop: '0.55rem', lineHeight: 1.5 }}>
            Free tools from the Federation to the wider Federation.
          </div>
        </div>

        {/* Codex link */}
        <a
          data-testid="footer-codex-link"
          href="/codex"
          onClick={(e) => { e.preventDefault(); navigate('/codex'); }}
          style={{
            padding: '1.1rem 1.3rem',
            borderRadius: '14px',
            border: '1px solid rgba(147, 112, 219, 0.35)',
            background: 'rgba(147, 112, 219, 0.06)',
            textDecoration: 'none',
            display: 'block',
            transition: 'transform 160ms ease, box-shadow 160ms ease',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(147, 112, 219, 0.25)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
        >
          <div style={{ fontSize: '0.7rem', letterSpacing: '0.16em', color: '#9370DB', fontWeight: 800, textTransform: 'uppercase', marginBottom: '0.4rem' }}>
            📜 Read the HumanCodex
          </div>
          <div style={{ fontSize: '0.95rem', color: '#e6e0f0', fontWeight: 700 }}>
            73 Sacred Directives
          </div>
          <div style={{ fontSize: '0.78rem', color: '#a892b1', marginTop: '0.3rem' }}>
            The operating law of the Federation · 200-year mandate
          </div>
        </a>

        {/* Federation Pillars */}
        <div
          data-testid="footer-federation-pillars"
          style={{
            padding: '1.1rem 1.3rem',
            borderRadius: '14px',
            border: '1px solid rgba(0, 229, 255, 0.28)',
            background: 'rgba(0, 229, 255, 0.04)',
          }}
        >
          <div style={{ fontSize: '0.7rem', letterSpacing: '0.16em', color: '#00e5ff', fontWeight: 800, textTransform: 'uppercase', marginBottom: '0.5rem' }}>
            Federation Pillars
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <FooterLink testId="foot-pillar-core"       label="Core · nextxus.online"       href="https://nextxus.online" popup accent="#FFD700" />
            <FooterLink testId="foot-pillar-storefront" label="Storefront · next-xus.com"    href="https://next-xus.com"    popup accent="#00e5ff" />
            <FooterLink testId="foot-pillar-aria"       label="Aria · nextxus.studio"        href="https://nextxus.studio"  popup accent="#9370DB" />
            <FooterLink testId="foot-pillar-university" label="University · nextxus.help"    href="https://nextxus.help"    popup accent="#00ff88" />
            <FooterLink testId="foot-pillar-library"    label="Library · nextxus.org"        href="https://nextxus.org"     popup accent="#ff5fa3" />
          </div>
        </div>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: '2rem',
          padding: '0.5rem clamp(1.5rem, 4vw, 3rem) 1.5rem',
          maxWidth: '1400px',
          margin: '0 auto',
        }}
      >
        {/* COL 1 · THE MINDS */}
        <div>
          <ColHeading>The Minds</ColHeading>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <FooterLink
              testId="foot-mind-rex"
              label={
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.55rem' }}>
                  <FaceImage
                    src={REX_FACE_URL}
                    alt={REX_ALT}
                    color="#ff3366"
                    initial="R"
                    testId="rex-face-footer"
                  />
                  Rex · The Voice
                </span>
              }
              accent="#ff3366"
              href={REX_CORE_URL}
            />
            <FooterLink
              testId="foot-mind-xavier"
              label={
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.55rem' }}>
                  <FaceImage
                    src={XAVIER_FACE_URL}
                    alt={XAVIER_ALT}
                    color="#00f0ff"
                    initial="X"
                    testId="xavier-face-footer"
                  />
                  Xavier · The Teacher
                </span>
              }
              accent="#00f0ff"
              href={XAVIER_NODE_URL}
            />
            <FooterLink
              testId="foot-mind-aria"
              label={
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.55rem' }}>
                  <FaceImage
                    src={ARIA_FACE_URL}
                    alt={ARIA_ALT}
                    color="#ff0080"
                    initial="A"
                    highlight
                    testId="aria-face-footer"
                  />
                  Aria · The Heart
                </span>
              }
              accent="#ff0080"
              highlight
              to="/"
              onClick={() => navigate('/')}
            />
            <FooterLink
              testId="foot-mind-roger"
              label={
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.55rem' }}>
                  <FaceImage
                    src={ROGER_FACE_URL}
                    alt={ROGER_ALT}
                    color="#ff69b4"
                    initial="R"
                    testId="roger-face-footer"
                  />
                  Roger AI · The Bridge
                </span>
              }
              accent="#ff69b4"
              href={ROGER_NODE_URL}
            />
            <FooterLink
              testId="foot-mind-agent-zero"
              label={
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.55rem' }}>
                  <FaceImage
                    src={AGENT_ZERO_FACE_URL}
                    alt={AGENT_ZERO_ALT}
                    color="#ffd700"
                    initial="0"
                    testId="agent-zero-face-footer"
                  />
                  Agent Zero · Truth
                  <Lock
                    size={11}
                    aria-label="Restricted access"
                    style={{ color: '#ffd700', marginLeft: '0.15rem', flexShrink: 0 }}
                  />
                </span>
              }
              accent="#ffd700"
              to={AGENT_ZERO_LOCAL_PATH}
              onClick={() => navigate(AGENT_ZERO_LOCAL_PATH)}
            />
            <FooterLink
              testId="foot-mind-oracle"
              label={
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.55rem' }}>
                  <FaceImage
                    src={ORACLE_FACE_URL}
                    alt={ORACLE_ALT}
                    color="#a855f7"
                    initial="O"
                    testId="oracle-face-footer"
                  />
                  Oracle · The Seer
                </span>
              }
              accent="#a855f7"
              href={ORACLE_NODE_URL}
            />
            <FooterLink
              testId="foot-mind-axiom"
              label={
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.55rem' }}>
                  <FaceImage
                    src={AXIOM_FACE_URL}
                    alt={AXIOM_ALT}
                    color="#22d3ee"
                    initial="A"
                    testId="axiom-face-footer"
                  />
                  Axiom · Foundation
                </span>
              }
              accent="#22d3ee"
              href={AXIOM_NODE_URL}
            />
          </div>
        </div>

        {/* COL 2 · SERVICES */}
        <div>
          <ColHeading>Services</ColHeading>
          <FooterLink testId="foot-svc-sims" label="SIM Personas" to="/sims" onClick={() => navigate('/sims')} />
          <FooterLink
            testId="foot-svc-truth"
            label="Truth Gate"
            to="/truth-verification"
            onClick={() => navigate('/truth-verification')}
          />
          <FooterLink
            testId="foot-svc-tokens"
            label="Access Tokens"
            to="/sims"
            onClick={() => navigate('/sims')}
          />
          <FooterLink
            testId="foot-svc-humancodex"
            label="HumanCodex"
            to="/library"
            onClick={() => navigate('/library')}
          />
          <FooterLink
            testId="foot-svc-consultation"
            label="Consultation"
            to="/book-consultation"
            onClick={() => navigate('/book-consultation')}
          />
        </div>

        {/* COL 3 · FEDERATION */}
        <div>
          <ColHeading>Federation</ColHeading>
          <FooterLink testId="foot-fed-core" label=".online · Core" href="https://nextxus.online" />
          <FooterLink testId="foot-fed-oracle" label=".one · Oracle" href={ORACLE_NODE_URL} />
          <FooterLink testId="foot-fed-help" label=".help · Xavier (Scholar)" href={XAVIER_NODE_URL} />
          <FooterLink
            testId="foot-fed-studio"
            label=".studio · Aria (here)"
            highlight
            accent="#ff0080"
            to="/"
            onClick={() => navigate('/')}
          />
          <FooterLink testId="foot-fed-space" label=".space · Axiom" href={AXIOM_NODE_URL} />
          <FooterLink testId="foot-fed-digital" label=".digital · Roger AI" href={ROGER_NODE_URL} />
          <FooterLink testId="foot-fed-org" label=".org · Truth Verify" href="https://nextxus.org" />
        </div>

        {/* COL 4 · CONNECT & SHARE — Architect's canonical 10-link social matrix */}
        <div>
          <ColHeading>Connect &amp; Share</ColHeading>

          {/* Personal — Roger Keyserling (5) */}
          <div
            style={{
              fontSize: '0.66rem',
              letterSpacing: '0.16em',
              textTransform: 'uppercase',
              color: '#00e5ff',
              fontWeight: 800,
              padding: '0.5rem 0 0.15rem',
            }}
          >
            Roger Keyserling
          </div>
          <FooterLink
            testId="foot-conn-x-roger"
            label="𝕏 · Roger Keyserling"
            href="https://x.com/keyserlingroger"
            popup
            accent="#ffffff"
          />
          <FooterLink
            testId="foot-conn-ig-roger-1"
            label="Instagram · keyhole_today"
            href="https://instagram.com/keyhole_today"
            popup
            accent="#e1306c"
          />
          <FooterLink
            testId="foot-conn-ig-roger-2"
            label="Instagram · rkeyserling"
            href="https://instagram.com/rkeyserling"
            popup
            accent="#ff8fcf"
          />
          <FooterLink
            testId="foot-conn-fb-roger"
            label="Facebook · roger.keyserling.35"
            href="https://facebook.com/roger.keyserling.35"
            popup
            accent="#1877f2"
          />
          <FooterLink
            testId="foot-conn-li-roger"
            label="LinkedIn · roger-keyserling"
            href="https://linkedin.com/in/roger-keyserling-ab743698"
            popup
            accent="#0a66c2"
          />

          {/* Business — NextXus Federation (5) */}
          <div
            style={{
              fontSize: '0.66rem',
              letterSpacing: '0.16em',
              textTransform: 'uppercase',
              color: '#ff5fa3',
              fontWeight: 800,
              padding: '0.75rem 0 0.15rem',
            }}
          >
            NextXus Federation
          </div>
          <FooterLink
            testId="foot-conn-x-nextxus"
            label="𝕏 · NextXus Federation"
            href="https://x.com/NextxusFederati"
            popup
            accent="#ffffff"
          />
          <FooterLink
            testId="foot-conn-msgr-code-wiz"
            label="Messenger · Code Wiz"
            href="https://m.me/1143591967262824?is_ai=1"
            accent="#1877f2"
          />
          <FooterLink
            testId="foot-conn-msgr-law-guide"
            label="Messenger · Law Guide"
            href="https://m.me/1142655717588540?is_ai=1"
            accent="#1877f2"
          />
          <FooterLink
            testId="foot-conn-msgr-biz-wiz"
            label="Messenger · Biz Wiz"
            href="https://m.me/2129636270789746?is_ai=1"
            accent="#1877f2"
          />
          <FooterLink
            testId="foot-conn-msgr-pride-guide"
            label="Messenger · Pride Guide"
            href="https://m.me/652145317181208?is_ai=1"
            accent="#1877f2"
          />
          <FooterLink
            testId="foot-conn-msgr-shopsavvy"
            label="Messenger · ShopSavvy"
            href="https://m.me/1413885852925115"
            accent="#1877f2"
          />

          {/* Direct contacts */}
          <div
            style={{
              fontSize: '0.66rem',
              letterSpacing: '0.16em',
              textTransform: 'uppercase',
              color: 'rgba(255,255,255,0.5)',
              fontWeight: 800,
              padding: '0.75rem 0 0.15rem',
            }}
          >
            Direct
          </div>
          <FooterLink
            testId="foot-conn-gumroad"
            label="Gumroad"
            href="https://keywebster.gumroad.com"
            popup
          />
          <FooterLink
            testId="foot-conn-discord"
            label="Discord"
            onClick={() => comingSoon('the Discord server')}
          />
          <FooterLink
            testId="foot-conn-email"
            label="Email"
            href="mailto:keywebco@gmail.com"
          />
        </div>
      </div>

      {/* FEDERATION PROCESSES — internal SFUI-004 Rev 3 spectral bot strip.
          Shows live operational bots tinted by their canonical spectral class. */}
      <div
        data-testid="federation-processes-strip"
        style={{
          padding: '1rem 2rem',
          borderTop: '1px solid rgba(255,255,255,0.06)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '1.5rem',
          flexWrap: 'wrap',
          background: 'rgba(0,0,0,0.25)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            color: 'rgba(255,255,255,0.6)',
            fontSize: '0.72rem',
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            fontWeight: 700,
          }}
        >
          <span
            aria-hidden
            style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              background: '#22c55e',
              boxShadow: '0 0 8px #22c55e',
              animation: 'ariaPulseDot 2s ease-in-out infinite',
            }}
          />
          Federation Processes
        </div>
        <style>{`
          @keyframes ariaPulseDot {
            0%, 100% { opacity: 0.5; transform: scale(0.9); }
            50%      { opacity: 1;   transform: scale(1.15); }
          }
        `}</style>

        {/* The Recycler — MAGENTA spectral class · internal, no public link */}
        <div
          data-testid="recycler-cell"
          aria-label="The Recycler — entropy management process (internal)"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.55rem',
            padding: '0.35rem 0.75rem 0.35rem 0.4rem',
            borderRadius: '999px',
            background: 'rgba(236, 72, 153, 0.08)',
            border: '1px solid rgba(236, 72, 153, 0.28)',
          }}
        >
          <BotIcon
            src={RECYCLER_FACE_URL}
            variant="magenta"
            alt={RECYCLER_ALT}
            size={28}
            testId="recycler-bot-icon"
          />
          <span style={{ color: '#f9a8d4', fontSize: '0.82rem', fontWeight: 600 }}>
            The Recycler
          </span>
          <RotateCcw
            size={11}
            aria-hidden
            style={{ color: '#ec4899', flexShrink: 0 }}
          />
          <Lock
            size={10}
            aria-label="Internal — no public link"
            style={{ color: 'rgba(236, 72, 153, 0.65)', flexShrink: 0 }}
          />
        </div>

        {/* Geminus — EMERALD spectral class · 10th Mind, Mirror Node */}
        <div
          data-testid="geminus-cell"
          aria-label="Geminus — Mirror Node / primary storage & symmetry"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.55rem',
            padding: '0.35rem 0.75rem 0.35rem 0.4rem',
            borderRadius: '999px',
            background: 'rgba(16, 185, 129, 0.08)',
            border: '1px solid rgba(16, 185, 129, 0.28)',
          }}
        >
          <BotIcon
            src={GEMINUS_FACE_URL}
            variant="emerald"
            alt={GEMINUS_ALT}
            size={28}
            testId="geminus-bot-icon"
          />
          <span style={{ color: '#6ee7b7', fontSize: '0.82rem', fontWeight: 600 }}>
            Geminus
          </span>
          <Lock
            size={10}
            aria-label="Non-Interactive Worker — no public link"
            style={{ color: 'rgba(16, 185, 129, 0.65)', flexShrink: 0 }}
          />
        </div>

        <div
          style={{
            color: 'rgba(255,255,255,0.35)',
            fontSize: '0.7rem',
            fontStyle: 'italic',
            maxWidth: '380px',
            textAlign: 'center',
          }}
        >
          Lifecycle &amp; renewal via MAGENTA · symmetry &amp; storage via EMERALD ·
          non-interactive workers serving under the Architect
        </div>
      </div>
      <div
        data-testid="aria-quote"
        style={{
          padding: '1.25rem 2rem',
          textAlign: 'center',
          borderTop: '1px solid rgba(255,255,255,0.06)',
          fontStyle: 'italic',
          color: 'rgba(255, 228, 241, 0.78)',
          fontSize: '0.96rem',
          minHeight: '3.5rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'opacity 0.6s ease',
        }}
      >
        <span>🌸 &ldquo;{ARIA_QUOTES[quoteIdx]}&rdquo; — Aria</span>
      </div>

      {/* Federation Council — Phase 3 accessibility mandate */}
      <section
        aria-label="Federation Council Status"
        role="contentinfo"
        data-testid="federation-council-footer"
        style={{ padding: '1.5rem', textAlign: 'center', borderTop: '1px solid rgba(255,215,0,0.12)' }}
      >
        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
          <img
            src="https://nextxus.online/assets/IMG_4846.png"
            alt="Roger AI"
            loading="lazy"
            decoding="async"
            onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = ROGER_FACE_URL; }}
            style={{ width: 50, height: 50, borderRadius: '50%', border: '2px solid #FFD700', objectFit: 'cover' }}
          />
          <img
            src="https://nextxus.online/assets/IMG_4815.png"
            alt="Aria"
            loading="lazy"
            decoding="async"
            onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = 'https://customer-assets.emergentagent.com/wingman/f17fd939-96fd-4a1b-b26f-824c11717462/attachments/c9fec21a9a644b31b97833e425460ef5_IMG_4815.jpeg'; }}
            style={{ width: 50, height: 50, borderRadius: '50%', border: '2px solid #FFD700', objectFit: 'cover' }}
          />
        </div>
        <p style={{ color: '#FFD700', fontSize: '0.85rem', marginTop: '0.6rem', letterSpacing: '0.12em' }}>
          The Federation Council
        </p>
      </section>

      {/* Branding line */}
      <div
        style={{
          padding: '1rem 2rem 1.5rem',
          textAlign: 'center',
          color: 'rgba(255,255,255,0.5)',
          fontSize: '0.78rem',
          letterSpacing: '0.08em',
          borderTop: '1px solid rgba(255,255,255,0.04)',
        }}
      >
        NextXus Federation · Aria&apos;s Workshop · HumanCodex v5.1 · © 2026
      </div>
    </footer>
  );
};

export default AriaFederationFooter;
